// §5 / Q44 date rendering for the shared export helpers — Table E item 23:
// "CSV and print turn dates into full UTC timestamps (csvExport.ts:17,
// printExport.ts:22)." Print renders the constitution's short form
// ('Oct 6, 2026', Central); CSV keeps a sortable Central-time value
// (YYYY-MM-DD, plus HH:MM for true timestamps) because spreadsheets sort
// on it — Codex ruling B, 2026-10-16.
//
// Detection is deliberately strict: only Date instances and strings that are
// EXACTLY a full ISO calendar date (optionally a full timestamp with zone)
// are rewritten — the pattern is anchored at both ends, so a note or name
// that merely begins with a date passes through whole (H1 fix). Ids, note
// text and any other value return null and fall through to the caller's
// plain-text path. A date-only string ('2026-11-01') is a CALENDAR DATE, not
// an instant: it renders exactly as written instead of being shifted a day
// by a midnight-UTC parse rendered in Central time.
const ISO_PREFIX = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

const CENTRAL = 'America/Chicago';

type CalendarParts = {y: number; m: number; d: number; hm: string | null};

// Central-time calendar parts for a true instant (Date or timestamp string).
function centralParts(date: Date): CalendarParts | null {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: CENTRAL,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    // h23, not hour12:false: some engines render midnight hour as '24' with
    // hour12:false (L1).
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const y = Number(get('year'));
  const m = Number(get('month'));
  const d = Number(get('day'));
  if (!y || !m || !d) return null;
  return {y, m, d, hm: get('hour') + ':' + get('minute')};
}

// Short form for the print header and date cells: 'Oct 6, 2026'. Built from
// the calendar parts over UTC so the month name never depends on the host
// timezone or a DST edge.
function shortFromParts({y, m, d}: CalendarParts): string {
  const text = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(Date.UTC(y, m - 1, d)));
  return text;
}

// Returns the export date for a value, or null when the value is not a date:
//   exportDateValue(new Date('2026-11-05T18:30:00Z')) => '2026-11-05 12:30'
//   exportDateValue('2026-11-01')                     => '2026-11-01'
//   exportDateValue('2026-11-01T09:00:00Z')           => '2026-10-31 21:00'
//   exportDateValue('cl-2026-note')                   => null
export function exportDateValue(value: unknown): string | null {
  if (value instanceof Date) {
    const p = centralParts(value);
    if (!p) return null;
    return p.hm ? `${p.y}-${String(p.m).padStart(2, '0')}-${String(p.d).padStart(2, '0')} ${p.hm}` : null;
  }
  if (typeof value !== 'string') return null;
  const match = ISO_PREFIX.exec(value);
  if (!match) return null;
  const [, y, m, d, hh, mm] = match;
  const month = Number(m);
  const day = Number(d);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  // Date-only: a calendar date, passed through exactly as written.
  if (hh === undefined || mm === undefined) return `${y}-${m}-${d}`;
  // True timestamp: convert the instant to the farm's Central time.
  const p = centralParts(new Date(value));
  if (!p) return `${y}-${m}-${d}`;
  return `${p.y}-${String(p.m).padStart(2, '0')}-${String(p.d).padStart(2, '0')} ${p.hm}`;
}

// Returns the §5 short date ('Oct 6, 2026') for a detected date value, or
// null when the value is not a date. Times are dropped: Q44's print header
// and table dates are calendar dates.
export function exportDateShort(value: unknown): string | null {
  const iso = exportDateValue(value);
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return shortFromParts({y, m, d, hm: null});
}
